import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, IconButton, Tooltip, Typography, Divider } from '@mui/material';
import { ChevronLeft, ChevronRight, Tune } from '@mui/icons-material';

export const PANEL_WIDTH = 340;
export const PANEL_WIDTH_COLLAPSED = 52;

/**
 * Remembers whether the options panel is collapsed, per page.
 */
export const useCollapsiblePanel = (storageKey, defaultCollapsed = false) => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored === null ? defaultCollapsed : stored === 'true';
    } catch (e) {
      return defaultCollapsed;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(collapsed));
    } catch (e) {
      // Storage unavailable - the preference just won't persist.
    }
  }, [storageKey, collapsed]);

  const toggle = useCallback(() => setCollapsed((prev) => !prev), []);

  return [collapsed, toggle];
};

/**
 * Collapsible configuration column. Collapsing hands the full width of the
 * workspace to the diagram; a summary rail keeps the current settings visible.
 */
const OptionsPanel = ({ collapsed, onToggle, title = 'Options', summary = [], footer, children }) => {
  if (collapsed) {
    return (
      <Card
        sx={{
          width: PANEL_WIDTH_COLLAPSED,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 1.5,
          gap: 1,
          overflow: 'hidden',
          transition: 'width 0.25s ease'
        }}
      >
        <Tooltip title={`Show ${title.toLowerCase()}`} placement="right">
          <IconButton size="small" onClick={onToggle} color="primary">
            <ChevronRight />
          </IconButton>
        </Tooltip>

        <Tune fontSize="small" color="action" />

        <Divider flexItem sx={{ my: 0.5 }} />

        {/* Vertical summary so collapsing never hides what is configured. */}
        <Tooltip
          title={summary.map((item) => `${item.label}: ${item.value}`).join(' \u00b7 ')}
          placement="right"
        >
          <Typography
            variant="caption"
            noWrap
            sx={{
              writingMode: 'vertical-rl',
              color: 'text.secondary',
              letterSpacing: '0.04em',
              mt: 0.5,
              maxHeight: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              cursor: 'default'
            }}
          >
            {summary.map((item) => item.value).join('  \u00b7  ')}
          </Typography>
        </Tooltip>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        width: { xs: '100%', md: PANEL_WIDTH },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        // On mobile the page scrolls, so the panel grows naturally instead of
        // creating a second scroll area inside the first.
        maxHeight: { xs: 'none', md: '100%' },
        overflow: 'hidden',
        transition: 'width 0.25s ease'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 0.25,
          flexShrink: 0
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {title}
        </Typography>
        <Tooltip title={`Hide ${title.toLowerCase()}`} placement="left">
          <IconButton size="small" onClick={onToggle}>
            <ChevronLeft />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: { xs: 'visible', md: 'auto' },
          overflowX: 'hidden',
          px: 2,
          pt: 0.75,
          pb: 1
        }}
      >
        {children}
      </Box>

      {/* Pinned action area. The settings above can grow - scope tags and
          application types only appear once they have loaded - and the primary
          action must not be pushed out of view when they do. */}
      {footer && (
        <>
          <Divider />
          <Box sx={{ flexShrink: 0, px: 2, py: 1 }}>{footer}</Box>
        </>
      )}
    </Card>
  );
};

export default OptionsPanel;
